"use client";
import React, { useState, useEffect } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";

const dataMix = [
  { name: "Renewables", value: 92, color: "#003350" },
  { name: "Other (fossil fuels)", value: 8, color: "#48DBB2" },
];

const dataImports = [
  { name: "Petroleum", value: 22.5, color: "#003350" },
  { name: "Other", value: 77.5, color: "#48DBB2" },
];

const legendItems = [
  { name: "Geothermal", color: "#003350" },
  { name: "Other (fossil fuels)", color: "#48DBB2" },
];

const DualDonutChart: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const [show, setShow] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isSmallMobile, setIsSmallMobile] = useState(false);

  useEffect(() => {
    if (isActive) {
      setTimeout(() => setShow(true), 200);
    } else {
      setShow(false);
    }

    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 1170);
      setIsSmallMobile(width < 480);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isActive]);

  if (!show) return <div className="w-full h-full bg-[#f9f9f9]" />;

  return (
    <div
      className="w-full flex flex-col bg-[#f9f9f9] overflow-hidden p-2"
      style={{ height: isMobile ? (isSmallMobile ? 500 : 480) : "100%" }}
    >
      {/* Top Chart (50% height) */}
      <div className="w-full h-1/2 flex flex-col items-center justify-center relative">
        <h3
          style={{
            textAlign: "center",
            fontSize: isSmallMobile ? 11 : isMobile ? 12 : 14,
            fontWeight: 600,
            marginBottom: isMobile ? 4 : 8,
            color: "#003350",
          }}
        >
          Electricity Generation Mix (2024)
        </h3>

        <div className="flex mobileMax:flex-col w-full h-[80%] items-center justify-center">
          {/* Pie Chart */}
          <div className="w-1/2 mobileMax:w-full h-full mobileMax:h-[70%] relative flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={dataMix}
                  cx="50%"
                  cy="50%"
                  innerRadius={isSmallMobile ? 28 : isMobile ? 35 : 45}
                  outerRadius={isSmallMobile ? 45 : isMobile ? 55 : 75}
                  startAngle={90}
                  endAngle={-270}
                  dataKey="value"
                  stroke="none"
                  isAnimationActive={true}
                >
                  {dataMix.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            {/* Inner Text */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span
                className="text-[#48DBB2] font-bold"
                style={{ fontSize: isSmallMobile ? 14 : isMobile ? 16 : 22, lineHeight: 1 }}
              >
                90%+
              </span>
              <span
                className="text-[#48DBB2] font-bold"
                style={{ fontSize: isSmallMobile ? 7 : isMobile ? 8 : 10, marginTop: 2 }}
              >
                RENEWABLES
              </span>
            </div>

            {/* Centered label container */}
            <div className="absolute top-1/2 left-1/2 w-0 h-0 flex items-center justify-center pointer-events-none">
              {/* Label for <10% */}
              <div
                className="absolute text-white font-semibold transform -rotate-[15deg]"
                style={{
                  top: isSmallMobile ? -36 : isMobile ? -50 : -64,
                  left: isSmallMobile ? -24 : isMobile ? -21 : -28,
                  fontSize: isSmallMobile ? 6 : 10,
                }}
              >
                &lt;10%
              </div>
            </div>
          </div>

          {/* Legend */}
          <div
            className="w-1/2 mobileMax:w-full flex flex-col mobileMax:flex-row mobileMax:justify-center mobileMax:items-center mobileMax:pl-0 justify-center pl-2"
            style={{
              gap: isSmallMobile ? 8 : isMobile ? 12 : 8,
              marginTop: isMobile ? 8 : 0,
            }}
          >
            {legendItems.map((item, index) => (
              <div key={index} className="flex items-center gap-2 whitespace-nowrap">
                <div
                  style={{
                    backgroundColor: item.color,
                    width: isMobile ? 10 : 14,
                    height: isMobile ? 10 : 14,
                  }}
                ></div>
                <span
                  className="text-gray-800 font-medium"
                  style={{ fontSize: isMobile ? 10 : 13 }}
                >
                  {item.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Chart (50% height) */}
      <div className="w-full h-1/2 flex flex-col items-center justify-center relative mt-2">
        <h3
          style={{
            textAlign: "center",
            fontSize: isSmallMobile ? 11 : isMobile ? 12 : 14,
            fontWeight: 600,
            marginBottom: isMobile ? 4 : 8,
            color: "#003350",
          }}
        >
          Petroleum Imports
          <br />
          as % of Total Import Bill
        </h3>

        <div className="w-full h-[80%] mobileMax:h-[70%] relative flex items-center justify-center">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={dataImports}
                cx="50%"
                cy="50%"
                innerRadius={isSmallMobile ? 35 : isMobile ? 45 : 55}
                outerRadius={isSmallMobile ? 55 : isMobile ? 70 : 85}
                startAngle={90}
                endAngle={-270}
                dataKey="value"
                stroke="#fff"
                strokeWidth={2}
                isAnimationActive={true}
              >
                {dataImports.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>

          {/* Inner Text */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span
              className="text-[#003350] font-bold"
              style={{ fontSize: isSmallMobile ? 15 : isMobile ? 18 : 24 }}
            >
              20-25%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DualDonutChart;
